import React, { useMemo } from "react";
import {
  ScrollView,
  TouchableOpacity,
  Text,
  Linking,
  View,
} from "react-native";
import LHOverlay from "../../../components/LHOverlay";
import lightStyles, {
  createHomeScreenOverlayContainerStyle,
  createHomeScreenThemeStyles,
} from "../HomeScreen.style";
import { TEXT } from "../../../constants/texts";
import SecurityIcon from "./icons/SecurityIcon";
import { useTheme } from "../../../theme";

interface SafetyTopicItem {
  id: string;
  title: string;
  messageText: string;
  category: string;
  order: number;
  link: {
    Description: string;
    Url: string;
  };
  image: {
    Description: string;
    Url: string;
  };
  createdDate: string;
  modifiedDate: string;
}

interface TrainingEssentialItem {
  id: string | number;
  title: string;
  urlLinkForBanner?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
}

interface SafetyTopicsOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  safetyTopics: SafetyTopicItem[] | TrainingEssentialItem[] | string[];
}

const SafetyTopicsOverlay: React.FC<SafetyTopicsOverlayProps> = ({
  visible,
  onDismiss,
  safetyTopics,
}) => {
  const { colors } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  const overlayContainerStyle = useMemo(
    () => createHomeScreenOverlayContainerStyle(colors),
    [colors]
  );
  // Helper function to handle URL opening with error handling
  const handleLinkPress = (url: string, description: string) => {
    if (url) {
      Linking.openURL(url).catch((err) => {
        console.error("An error occurred opening the link:", err);
        console.log("Failed to open \"" + description + "\". Please try again later.");
      });
    } else {
      console.log("No URL available for: " + description);
    }
  };
  // Function to check if item is SharePoint safety format
  const isNewFormat = (
    item: SafetyTopicItem | TrainingEssentialItem | string
  ): item is SafetyTopicItem => {
    return typeof item === "object" && item !== null && "link" in item;
  };

  // Function to check if item is training essential format
  const isTrainingEssential = (
    item: SafetyTopicItem | TrainingEssentialItem | string
  ): item is TrainingEssentialItem => {
    return typeof item === "object" && item !== null && "title" in item && !("link" in item);
  };

  // Check if we have data to display
  const hasData = safetyTopics && safetyTopics.length > 0;

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title='All Safety Topics'
      containerStyle={overlayContainerStyle}
      headerPaddingHorizontal={24}
      hideFooter={true}
      size="large"
    >
      <ScrollView style={lightStyles.overlayContent}>
        {hasData ? (
          safetyTopics.map((topic, index) => {
            if (isNewFormat(topic)) {
              // SharePoint safety format
              const hasLink = topic.link?.Url && topic.link?.Description;
              return (
                <View key={topic.id || index}>
                  <TouchableOpacity
                    activeOpacity={0.8}
                    style={lightStyles.linkRow}
                    onPress={() => {
                      if (hasLink) {
                        handleLinkPress(topic.link.Url, topic.link.Description);
                      } else {
                        console.log('Navigating to blank page for: ' + topic.title);
                      }
                    }}
                  >
                    <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
                      <SecurityIcon size={16} color="#005CBC" />
                    </View>
                    <View style={lightStyles.linkTextContainer}>
                      <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]}>
                        {hasLink ? topic.link.Description : topic.title}
                      </Text>
                    </View>
                  </TouchableOpacity>
                  <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
                </View>
              );
            } else if (isTrainingEssential(topic)) {
              // Training essential format
              const url = topic.urlLinkForBanner || '';
              const hasLink = Boolean(url);
              return (
                <View key={topic.id || index}>
                  <TouchableOpacity
                    activeOpacity={0.8}
                    style={lightStyles.linkRow}
                    onPress={() => {
                      if (hasLink) {
                        handleLinkPress(url, topic.title);
                      } else {
                        console.log('Navigating to blank page for: ' + topic.title);
                      }
                    }}
                  >
                    <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
                      <SecurityIcon size={16} color="#005CBC" />
                    </View>
                    <View style={lightStyles.linkTextContainer}>
                      <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]}>{topic.title}</Text>
                    </View>
                  </TouchableOpacity>
                  <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
                </View>
              );
            } else {
              // Legacy string format
              return (
                <View key={index}>
                  <TouchableOpacity
                    activeOpacity={0.8}
                    style={lightStyles.linkRow}
                    onPress={() => console.log('Navigating to blank page for: ' + topic)}
                  >
                    <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
                      <SecurityIcon size={16} color="#005CBC" />
                    </View>
                    <View style={lightStyles.linkTextContainer}>
                      <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]}>{topic as string}</Text>
                    </View>
                  </TouchableOpacity>
                  <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
                </View>
              );
            }
          })
        ) : (
          <View style={lightStyles.noDataContainer}>
            <Text style={[lightStyles.noDataText, themeStyles.noDataText]}>{TEXT.COMMON.NO_DATA}</Text>
          </View>
        )}
      </ScrollView>
      {/* Close button removed as requested */}
    </LHOverlay>
  );
};

export default SafetyTopicsOverlay;
